/**
 * API Client - Neural Network Communication Layer
 * Version: 6.2.0
 */
import axios, { AxiosInstance, AxiosError } from 'axios'

// API Configuration
const API_BASE_URL = import.meta.env.VITE_API_URL || ''

// Create axios instance with default config
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor
apiClient.interceptors.request.use(
  (config) => {
    // Add auth token if available
    const token = localStorage.getItem('auth_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => Promise.reject(error)
)

// Response interceptor
apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      // Handle unauthorized - redirect to landing page
      localStorage.removeItem('auth_token')
      localStorage.removeItem('refresh_token')
      localStorage.removeItem('user_id')
      localStorage.removeItem('username')
      localStorage.removeItem('user_role')
      window.location.href = '/landing'
    }
    return Promise.reject(error)
  }
)

export default apiClient

// API Response types
export interface ApiResponse<T = unknown> {
  success: boolean
  data?: T
  error?: string
  message?: string
}

export interface PaginatedResponse<T> extends ApiResponse<T> {
  total: number
  page: number
  limit: number
}

// Stats types
export interface Stats {
  totalUsers: number
  activeUsers: number
  exchanges: number
  strategies: number
  brokers: number
  healthStatus: string
}

// Broker types
export interface Broker {
  id: string
  name: string
  status: 'connected' | 'ready' | 'available'
  icon?: string
  traders?: string
  createdAt?: string
}

// Strategy types
export interface Strategy {
  id: string
  name: string
  description: string
  category: string
  winRate: string
  trades: string
  status: 'active' | 'draft' | 'archived'
  createdAt?: string
}

// Backtest types
export interface BacktestConfig {
  strategyId: string
  timeframe: string
  dateRange: string
  initialCapital: number
}

export interface BacktestResult {
  totalReturn: number
  winRate: number
  sharpeRatio: number
  maxDrawdown: number
  trades: number
  profitFactor: number
}

// Trade types
export interface Trade {
  id: string
  symbol: string
  type: 'BUY' | 'SELL'
  quantity: number
  price: number
  timestamp: string
  broker?: string
  strategy?: string
}